import {LocationDto, MemberLocationPermissionDto} from "../../../../generated/dashboard-client";
import {ACCESS_LEVELS, AccessLevel, AddOn, ADD_ONS, Preset, clearInvalidAddOns, matchPreset} from "./permissionModel";

export interface PresetBadgeInfo {
  key: string;
  label: string;
  level: AccessLevel | null;
  addOns: AddOn[];
  description: string;
}

export interface LocationAccess {
  locationId: number;
  name: string;
  city: string;
  badge: PresetBadgeInfo;
}

export interface UserFilters {
  locationIds: number[];
  presetIds: string[];
  permissions: string[];
}

const CUSTOM_BADGE_KEY = "custom";
const LIMITED_BADGE_KEY = "limited";

export const NO_FILTERS: UserFilters = {locationIds: [], presetIds: [], permissions: []};

export const FILTER_PERMISSIONS: {key: string; label: string}[] = [
  {key: "viewOnly", label: "View Only"},
  {key: "editMenuData", label: "Edit Menu Data"},
  {key: "editDesigns", label: "Edit Designs"},
  {key: "createTemplates", label: "Create Templates"},
  {key: "publish", label: "Publish"},
  {key: "orderPrint", label: "Order Prints"},
];

export function filterCount(filters: UserFilters): number {
  return filters.locationIds.length + filters.presetIds.length + filters.permissions.length;
}

function levelOf(row: MemberLocationPermissionDto): AccessLevel | null {
  if (row.viewOnly) return "viewOnly";
  if (row.dataOnly || row.itemLibraryOnly) return "editMenuData";
  if (row.downloadAndPublish) return "editDesigns";
  return null;
}

function addOnsOf(row: MemberLocationPermissionDto, level: AccessLevel | null): AddOn[] {
  const selected = ADD_ONS.filter((a) => {
    if (a.key === "createTemplates") return !!row.createTemplate;
    if (a.key === "publish") return !!row.publish;
    if (a.key === "orderPrint") return !!row.orderPrint;
    return !!row.holidayTemplates;
  }).map((a) => a.key);
  return clearInvalidAddOns(level, selected);
}

export function badgeFor(row: MemberLocationPermissionDto, presets: Preset[]): PresetBadgeInfo {
  const level = levelOf(row);
  const addOns = addOnsOf(row, level);
  const preset = matchPreset(presets, level, addOns);
  const description = ACCESS_LEVELS.find((l) => l.key === level)?.description ?? "";
  return {
    key: preset?.id ?? (level ? CUSTOM_BADGE_KEY : LIMITED_BADGE_KEY),
    label: preset?.name ?? (level ? "Custom" : "Limited Access"),
    level,
    addOns,
    description,
  };
}

export function locationAccessList(
  rows: MemberLocationPermissionDto[],
  locations: LocationDto[],
  presets: Preset[]
): LocationAccess[] {
  const byId = new Map(locations.map((l) => [l.internalId, l]));
  return rows
    .map((row) => {
      const location = byId.get(row.locationId);
      return {
        locationId: row.locationId!,
        name: location?.name ?? `Location ${row.locationId}`,
        city: [location?.city, location?.state].filter(Boolean).join(", "),
        badge: badgeFor(row, presets),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}
